import { useEffect, useMemo } from 'react';

import { keys, groupArrayBy } from '@direct-frontend/stdlib';
import cn from 'classnames';
import { useToaster } from 'react-hot-toast';

import { LayerFixedPriorities, useNewLayer, layerManagerIgnoreOnDismissClassName } from '@direct-frontend/components';
import { defaultPosition } from '../toast';

import styles from './ToastContainer.module.css';

/**
 * Контейнер для показа уведомлений-тостов.
 * Должен быть встроен в приложение единственный раз.
 */
export function ToastContainer() {
    const { toasts, handlers } = useToaster();
    const { startPause, endPause } = handlers;
    const toastsByPosition = useMemo(() => groupArrayBy(toasts, toast => toast.position ?? defaultPosition), [toasts]);

    useEffect(() => {
        const handleBlur = () => {
            /** Если на странице есть iframe, то не ставим на паузу при блюре на него */
            if (document.activeElement?.tagName !== 'IFRAME') {
                startPause();
            }
        };
        window.addEventListener('focus', endPause);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('focus', endPause);
            window.removeEventListener('blur', handleBlur);
        };
    }, [startPause, endPause]);

    const layerManager = useNewLayer({
        priority: LayerFixedPriorities.Toast,
    });

    return (
        <>
            {keys(toastsByPosition).map(position => {
                const toasts = toastsByPosition[position];

                if (!toasts?.length) {
                    return null;
                }

                const isTop = position.startsWith('top');
                const isBottom = position.startsWith('bottom');
                const isLeft = position.endsWith('left');
                const isCenter = position.endsWith('center');
                const isRight = position.endsWith('right');

                return (
                    <div
                        key={position}
                        onMouseEnter={startPause}
                        onMouseLeave={endPause}
                        className={cn(
                            styles.container,
                            isTop && styles.containerTop,
                            isBottom && styles.containerBottom,
                            isLeft && styles.containerLeft,
                            isCenter && styles.containerCenter,
                            isRight && styles.containerRight,
                            layerManagerIgnoreOnDismissClassName
                        )}
                        style={{
                            zIndex: layerManager.zIndex,
                        }}
                        data-testid="ToastContainer"
                    >
                        {toasts.map(toast =>
                            typeof toast.message === 'function' ? (
                                <div
                                    key={toast.id}
                                    className={cn(
                                        styles.toastWrapper,
                                        toast.visible && styles.toastWrapperVisible,
                                        isLeft && styles.toastWrapperLeft,
                                        isCenter && styles.toastWrapperCenter,
                                        isRight && styles.toastWrapperRight
                                    )}
                                >
                                    {toast.message({ ...toast, className: styles.toast })}
                                </div>
                            ) : null
                        )}
                    </div>
                );
            })}
        </>
    );
}
